import { useCallback } from 'react'
import { useRouter } from 'next/router'
import analyticsService from '@/lib/services/analytics'
import {
  EventName,
  EventCategory,
  TrackingEvent,
  TrackingProperties,
  UserProfile,
  OrderProperties,
  ExperienceProperties,
  ErrorProperties,
  SearchProperties,
} from '@/types/analytics'

export const useAnalytics = () => {
  const router = useRouter()

  // Note: Initialization and automatic page tracking are handled in _app.tsx
  // This hook only provides the analytics methods for manual tracking

  const identify = useCallback((userId: string, userProfile?: UserProfile) => {
    analyticsService.identify(userId, userProfile)
  }, [])

  const track = useCallback(
    (eventName: EventName | string, properties?: TrackingProperties) => {
      analyticsService.track(eventName, properties)
    },
    []
  )

  const trackEvent = useCallback((event: TrackingEvent) => {
    analyticsService.trackEvent(event)
  }, [])

  const trackPageView = useCallback(
    (pageName?: string, properties?: TrackingProperties) => {
      const page = pageName || router.asPath
      analyticsService.trackPageView(page, {
        ...properties,
        locale: router.locale,
      })
    },
    [router]
  )

  const trackOrder = useCallback(
    (eventName: EventName, properties: OrderProperties) => {
      analyticsService.track(eventName, {
        ...properties,
        category: EventCategory.Order,
      })
    },
    []
  )

  const trackExperience = useCallback(
    (eventName: EventName, properties: ExperienceProperties) => {
      analyticsService.track(eventName, {
        ...properties,
        category: EventCategory.Experience,
      })
    },
    []
  )

  const trackError = useCallback((properties: ErrorProperties) => {
    analyticsService.track(EventName.ErrorOccurred, {
      ...properties,
      category: EventCategory.Error,
    })
  }, [])

  const trackSearch = useCallback((properties: SearchProperties) => {
    analyticsService.track(EventName.Search, {
      ...properties,
      category: EventCategory.Search,
    })
  }, [])

  const trackRevenue = useCallback(
    (amount: number, properties?: TrackingProperties) => {
      analyticsService.trackRevenue(amount, properties)
    },
    []
  )

  const timeEvent = useCallback((eventName: EventName | string) => {
    analyticsService.timeEvent(eventName)
  }, [])

  const reset = useCallback(() => {
    analyticsService.reset()
  }, [])

  const optOut = useCallback(() => {
    analyticsService.optOut()
  }, [])

  const optIn = useCallback(() => {
    analyticsService.optIn()
  }, [])

  return {
    isInitialized: analyticsService.isInitialized(),
    identify,
    track,
    trackEvent,
    trackPageView,
    trackOrder,
    trackExperience,
    trackError,
    trackSearch,
    trackRevenue,
    timeEvent,
    reset,
    optOut,
    optIn,
  }
}

// Simplified hook for just tracking events
export const useTrackEvent = () => {
  const { track, trackEvent } = useAnalytics()
  return { track, trackEvent }
}

// Hook for tracking page views only (manual use only)
export const useTrackPageView = () => {
  const { trackPageView } = useAnalytics()

  // Note: No automatic tracking on mount - use this hook only for manual page view tracking
  // Automatic page view tracking is handled by useAnalytics hook via router events

  return trackPageView
}

// Hook for tracking errors
export const useTrackError = () => {
  const { trackError } = useAnalytics()

  const trackErrorWithContext = useCallback(
    (error: Error | string, context?: Record<string, any>) => {
      trackError({
        errorMessage: typeof error === 'string' ? error : error.message,
        errorStack: typeof error === 'object' ? error.stack : undefined,
        url: window.location.href,
        ...context,
      })
    },
    [trackError]
  )

  return trackErrorWithContext
}
